import { Check, ArrowRight, Crown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useCurrency, CurrencyToggle } from "@/lib/currency";

const PLANS = [
  {
    id: "freemium",
    title: "Freemium",
    price_usd: 0,
    cadence: "para siempre",
    description:
      "Para conocer la plataforma con un cronómetro activo de 3 minutos.",
    features: [
      "3 minutos de interacción activa",
      "Editor completo + copiloto Claude Sonnet",
      "Validar coherencia con GPT-4o",
      "Vista previa de plantilla universitaria",
      "Tu progreso se guarda automáticamente",
    ],
    cta: "Empezar gratis",
    href: "/signup",
    accent: "ink",
  },
  {
    id: "premium",
    title: "Premium · 1 proyecto",
    price_usd: 69,
    cadence: "pago único · de por vida",
    description:
      "Desbloqueo de por vida para 1 tesis hasta que la exportes terminada.",
    features: [
      "Generación de texto ilimitada",
      "RAG con OpenAlex (fuentes reales con DOI)",
      "Simulador de jurado completo",
      "Exportar DOCX con formato APA/Vancouver",
      "Métrica antiplagio + reescritura humana",
      "Sello criptográfico SHA-256 ilimitado",
      "Sin suscripción, sin renovación",
    ],
    cta: "Comprar acceso de por vida",
    href: "/pricing",
    accent: "forest",
    featured: true,
  },
];

export default function PricingTeaser() {
  const { format, currency } = useCurrency();
  return (
    <section
      id="precios"
      data-testid="section-pricing"
      className="relative scroll-mt-24 bg-paper-2/40 border-y border-rule"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper font-medium">
              Precios
            </p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.05] text-balance">
              Un solo pago. <span className="italic text-forest">Hasta que defiendas tu tesis</span>.
            </h2>
            <p className="mt-4 text-[15px] text-ink-soft max-w-xl leading-relaxed">
              Sin suscripción ni renovaciones. Pruebas 3 minutos gratis y, cuando estés listo, desbloqueas tu tesis con un solo pago.
            </p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1.5">
            <span className="text-[10px] tracking-[0.22em] uppercase text-ink-soft font-medium">
              Mostrar precios en
            </span>
            <CurrencyToggle testid="pricing-teaser-currency" />
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {PLANS.map((p) => (
            <article
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl p-7 lg:p-8 flex flex-col ${
                p.featured
                  ? "bg-[#1d2c22] text-paper ring-1 ring-[#E0C58A]/30 shadow-[0_30px_80px_-30px_rgba(20,32,24,0.5)]"
                  : "bg-white border border-rule"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-[#E0C58A] text-[#1d2c22] px-3 py-1 text-[10.5px] tracking-[0.18em] uppercase font-medium">
                  <Crown size={11} /> Recomendado
                </span>
              )}
              <p
                className={`text-[10px] tracking-[0.28em] uppercase font-medium ${
                  p.featured ? "text-[#E0C58A]" : "text-copper"
                }`}
              >
                {p.title}
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  className={`font-serif text-5xl ${p.featured ? "text-paper" : "text-ink"}`}
                >
                  {p.price_usd === 0
                    ? currency === "MXN"
                      ? "$0 MXN"
                      : "$0"
                    : format(p.price_usd)}
                </span>
                <span
                  className={`text-[12.5px] ${
                    p.featured ? "text-paper/65" : "text-ink-soft"
                  }`}
                >
                  {p.cadence}
                </span>
              </div>
              <p
                className={`mt-3 text-[13.5px] leading-relaxed max-w-sm ${
                  p.featured ? "text-paper/75" : "text-ink-soft"
                }`}
              >
                {p.description}
              </p>

              <ul className="mt-6 flex flex-col gap-2.5">
                {p.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-2 text-[13px] ${
                      p.featured ? "text-paper/90" : "text-ink"
                    }`}
                  >
                    <span
                      className={`mt-0.5 grid place-items-center h-4 w-4 shrink-0 rounded-full ${
                        p.featured ? "bg-[#E0C58A] text-[#1d2c22]" : "bg-forest text-paper"
                      }`}
                    >
                      <Check size={10} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to={p.href}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-medium transition-all ${
                  p.featured
                    ? "bg-[#E0C58A] text-[#1d2c22] hover:bg-[#EBD8A8]"
                    : "border border-forest text-forest hover:bg-forest hover:text-paper"
                }`}
              >
                {p.cta}
                <ArrowRight size={13} />
              </Link>
            </article>
          ))}
        </div>

        <p className="mt-10 text-[12px] text-ink-soft text-center max-w-xl mx-auto">
          Sin contratos. Tu pago aplica para un proyecto de tesis hasta que lo exportes terminado. Mantienes acceso a todos los sellos criptográficos generados.
        </p>
      </div>
    </section>
  );
}
